import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Play, Stop, Clock, MapPin, Plus, PencilSimple, Trash, X, Check, ClockCounterClockwise } from "@phosphor-icons/react";

function fmtElapsed(startIso) {
  if (!startIso) return "00:00";
  const start = new Date(startIso).getTime();
  const s = Math.max(0, Math.floor((Date.now() - start) / 1000));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  const pad = (n) => String(n).padStart(2, "0");
  return h > 0 ? `${h}:${pad(m)}:${pad(sec)}` : `${pad(m)}:${pad(sec)}`;
}

function fmtHm(min) {
  const m = Math.round(min || 0);
  return `${Math.floor(m / 60)}h${String(m % 60).padStart(2, "0")}`;
}

function toLocalInput(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function fmtEntry(e) {
  const s = new Date(e.started_at);
  const en = e.ended_at ? new Date(e.ended_at) : null;
  const day = s.toLocaleDateString("fr-FR", { weekday: "short", day: "2-digit", month: "short" });
  const t1 = s.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });
  const t2 = en ? en.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" }) : "…";
  return `${day} · ${t1} → ${t2}`;
}

export default function InterventionTimer({ interventionId, onUpdate }) {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [now, setNow] = useState(Date.now());
  const [editing, setEditing] = useState(null); // {mode: 'add'|'edit', entry?}

  const load = async () => {
    try { const r = await api.get(`/interventions/${interventionId}/timer`); setStatus(r.data); }
    catch { setStatus({ active_timer: null, entries: [], actual_hours: 0 }); }
  };
  useEffect(() => { load(); }, [interventionId]);
  useEffect(() => {
    if (status?.active_timer) {
      const t = setInterval(() => setNow(Date.now()), 1000);
      return () => clearInterval(t);
    }
  }, [status?.active_timer]);

  // Web browser GPS captation is disabled — the chrono starts/stops without
  // sending coordinates. Mobile app already sends its own GPS via the beacon.
  const withPos = () => Promise.resolve({});

  const start = async () => {
    setBusy(true);
    try {
      const pos = await withPos();
      await api.post(`/interventions/${interventionId}/timer/start`, { ...pos, method: "manual" });
      toast.success("Chrono démarré");
      await load();
      onUpdate?.();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const stop = async () => {
    setBusy(true);
    try {
      const pos = await withPos();
      const r = await api.post(`/interventions/${interventionId}/timer/stop`, { ...pos, method: "manual" });
      toast.success(`+${r.data.duration_min} min · Total ${r.data.actual_hours} h`);
      await load();
      onUpdate?.();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const removeEntry = async (entry) => {
    if (!window.confirm(`Supprimer cette session (${fmtEntry(entry)}) ?`)) return;
    try {
      await api.delete(`/interventions/${interventionId}/time-entries/${entry.id}`);
      toast.success("Session supprimée");
      await load();
      onUpdate?.();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  if (!status) return null;
  const running = !!status.active_timer;
  const elapsed = running ? fmtElapsed(status.active_timer.started_at) : "";
  const entries = status.entries || [];
  // Force re-render every second when running
  void now;

  return (
    <div className="rounded-2xl bg-white/[0.03] border border-white/10 p-3 space-y-3" data-testid="intervention-timer">
      <div className="flex items-center gap-3">
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${running ? "bg-red-500/20 animate-pulse" : "bg-white/5"}`}>
          <Clock size={20} weight="duotone" className={running ? "text-red-400" : "text-white/50"} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-widest text-white/50">Chrono terrain</div>
          <div className="font-mono text-lg font-bold tabular-nums">
            {running ? elapsed : `${(status.actual_hours || 0).toFixed(2)} h total`}
          </div>
          {status.active_timer?.method === "gps_auto" && (
            <div className="text-[10px] text-emerald-400 flex items-center gap-1"><MapPin size={9} weight="fill" /> Auto-GPS</div>
          )}
        </div>
        {running ? (
          <button onClick={stop} disabled={busy} className="px-4 h-11 rounded-full bg-red-500 hover:bg-red-600 flex items-center gap-1.5 text-sm font-semibold active:scale-95 disabled:opacity-50" data-testid="timer-stop">
            <Stop size={14} weight="fill" /> Stop
          </button>
        ) : (
          <button onClick={start} disabled={busy} className="px-4 h-11 rounded-full bg-emerald-600 hover:bg-emerald-700 flex items-center gap-1.5 text-sm font-semibold active:scale-95 disabled:opacity-50" data-testid="timer-start">
            <Play size={14} weight="fill" /> Démarrer
          </button>
        )}
      </div>

      {/* Sessions list + manual add */}
      <div>
        <div className="flex items-center justify-between mb-1.5">
          <div className="text-[10px] uppercase tracking-widest text-white/50 flex items-center gap-1.5">
            <ClockCounterClockwise size={11} /> Sessions ({entries.length})
          </div>
          <button
            onClick={() => setEditing({ mode: "add" })}
            className="h-7 px-2 rounded-md bg-white/5 hover:bg-white/10 text-[11px] inline-flex items-center gap-1"
            data-testid="timer-add-manual"
          >
            <Plus size={11} weight="bold" /> Ajouter
          </button>
        </div>
        {entries.length === 0 ? (
          <div className="text-[11px] text-white/40 italic">Aucune session enregistrée. Utilise le chrono ou ajoute manuellement.</div>
        ) : (
          <div className="rounded-lg border border-white/10 overflow-hidden">
            {entries.map((e) => (
              <div key={e.id} className="flex items-center gap-2 px-2.5 py-1.5 border-b border-white/5 last:border-b-0 text-xs" data-testid={`timer-entry-${e.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="truncate">{fmtEntry(e)}</div>
                  <div className="text-[10px] text-white/40 flex items-center gap-1.5">
                    <span>{e.user_name || "—"}</span>
                    {e.method === "manual_entry" && <span className="uppercase tracking-widest text-amber-400/70">manuel</span>}
                    {e.note && <span className="italic truncate">{e.note}</span>}
                  </div>
                </div>
                <div className="text-right tabular-nums font-semibold text-white/80 shrink-0">{fmtHm(e.duration_min)}</div>
                <button
                  onClick={() => setEditing({ mode: "edit", entry: e })}
                  className="h-6 w-6 rounded-md bg-white/5 hover:bg-white/10 text-white/60 flex items-center justify-center"
                  data-testid={`timer-entry-edit-${e.id}`}
                  title="Modifier"
                >
                  <PencilSimple size={10} />
                </button>
                <button
                  onClick={() => removeEntry(e)}
                  className="h-6 w-6 rounded-md bg-white/5 hover:bg-red-500/20 hover:text-red-400 text-white/60 flex items-center justify-center"
                  data-testid={`timer-entry-delete-${e.id}`}
                  title="Supprimer"
                >
                  <Trash size={10} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {editing && (
        <ManualEntryDialog
          interventionId={interventionId}
          entry={editing.entry}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); onUpdate?.(); }}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Manual entry dialog — add or edit a time_entry for the current user.
// ---------------------------------------------------------------------------
function ManualEntryDialog({ interventionId, entry, onClose, onSaved }) {
  const isEdit = !!entry;
  const today = new Date();
  const defaultStart = entry?.started_at ? toLocalInput(entry.started_at) : (() => {
    const d = new Date(today); d.setHours(8, 0, 0, 0); return toLocalInput(d.toISOString());
  })();
  const defaultEnd = entry?.ended_at ? toLocalInput(entry.ended_at) : (() => {
    const d = new Date(today); d.setHours(12, 0, 0, 0); return toLocalInput(d.toISOString());
  })();

  const [startLocal, setStartLocal] = useState(defaultStart);
  const [endLocal, setEndLocal] = useState(defaultEnd);
  const [note, setNote] = useState(entry?.note || "");
  const [saving, setSaving] = useState(false);

  const durationMin = (() => {
    if (!startLocal || !endLocal) return 0;
    const d = (new Date(endLocal).getTime() - new Date(startLocal).getTime()) / 60000;
    return Math.max(0, Math.round(d));
  })();

  const save = async () => {
    if (!startLocal || !endLocal) { toast.error("Renseigne les 2 horaires"); return; }
    if (new Date(endLocal) <= new Date(startLocal)) { toast.error("L'heure de fin doit être après le début"); return; }
    setSaving(true);
    try {
      const body = {
        started_at: new Date(startLocal).toISOString(),
        ended_at: new Date(endLocal).toISOString(),
        note: note || null,
      };
      if (isEdit) {
        await api.patch(`/interventions/${interventionId}/time-entries/${entry.id}`, body);
        toast.success("Session modifiée");
      } else {
        await api.post(`/interventions/${interventionId}/time-entries`, body);
        toast.success("Session ajoutée");
      }
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-end md:items-center justify-center p-4" data-testid="timer-entry-dialog">
      <div className="w-full max-w-sm rounded-2xl bg-[#0A0A0A] border border-white/10 shadow-2xl overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-white/40">Chrono</div>
            <div className="font-semibold">{isEdit ? "Modifier la session" : "Ajouter des heures"}</div>
          </div>
          <button onClick={onClose} className="h-8 w-8 rounded-lg hover:bg-white/10 flex items-center justify-center">
            <X size={14} />
          </button>
        </div>
        <div className="p-4 space-y-3">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-white/50 mb-1">Début</div>
            <input
              type="datetime-local"
              value={startLocal}
              onChange={(e) => setStartLocal(e.target.value)}
              className="w-full h-10 rounded-lg bg-white/5 border border-white/10 px-3 text-sm focus:outline-none focus:border-[#005CFF]"
              data-testid="timer-entry-start"
            />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-white/50 mb-1">Fin</div>
            <input
              type="datetime-local"
              value={endLocal}
              onChange={(e) => setEndLocal(e.target.value)}
              className="w-full h-10 rounded-lg bg-white/5 border border-white/10 px-3 text-sm focus:outline-none focus:border-[#005CFF]"
              data-testid="timer-entry-end"
            />
          </div>
          <div className="rounded-lg bg-[#005CFF]/10 border border-[#005CFF]/30 px-3 py-2 text-center">
            <div className="text-[10px] uppercase tracking-widest text-white/50">Durée calculée</div>
            <div className="font-bold tabular-nums text-lg">{fmtHm(durationMin)}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-white/50 mb-1">Note (optionnel)</div>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={2}
              placeholder="Ex : oubli du chrono ce matin"
              className="w-full rounded-lg bg-white/5 border border-white/10 px-3 py-2 text-sm resize-none focus:outline-none focus:border-[#005CFF]"
              data-testid="timer-entry-note"
            />
          </div>
        </div>
        <div className="flex items-center justify-end gap-2 px-4 py-3 border-t border-white/10 bg-white/[0.02]">
          <button onClick={onClose} className="h-9 px-3 rounded-lg text-sm bg-white/5 hover:bg-white/10">Annuler</button>
          <button
            onClick={save}
            disabled={saving || durationMin <= 0}
            className="h-9 px-4 rounded-lg text-sm font-semibold bg-[#005CFF] hover:bg-[#0047CC] disabled:opacity-40 inline-flex items-center gap-1.5"
            data-testid="timer-entry-save"
          >
            <Check size={13} weight="bold" /> {saving ? "…" : (isEdit ? "Modifier" : "Ajouter")}
          </button>
        </div>
      </div>
    </div>
  );
}
